import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import SwyperLogo from '../components/SwypeRLogo';
import axios from 'axios';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnifiedAuth = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userCount, setUserCount] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);

  // Navigate to /swipe when user is authenticated after login
  useEffect(() => {
    if (user && shouldNavigate) {
      navigate('/swipe');
      setShouldNavigate(false);
    }
  }, [user, shouldNavigate, navigate]);

  // Fetch user count on component mount
  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats/user-count`);
        setUserCount(response.data.count);
      } catch (error) {
        // Silently fail - user count is non-critical
      }
    };
    
    fetchUserCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API_URL is constant, intentionally empty

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Check if email exists
      const checkResponse = await axios.post(`${API_URL}/api/auth/check-email`, {
        email: email.toLowerCase().trim()
      });

      if (checkResponse.data.exists) {
        // Existing user - show password field
        setUserExists(true);
        setStep('password');
      } else {
        // New user - send verification code
        await axios.post(`${API_URL}/api/auth/send-code`, {
          email: email.toLowerCase().trim()
        });
        
        // Navigate to verification page
        navigate('/verify-code', { state: { email: email.toLowerCase().trim() } });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast({
        title: "Password Required",
        description: "Please enter your password",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      await login(email, password);
      setShouldNavigate(true); // Trigger navigation via useEffect
      setIsLoading(false);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.message || "Invalid password. Please try again.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setUserExists(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50 pt-[4px] px-4 pb-[4px]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg pt-0 px-8 pb-8">
          <div className="flex flex-col items-center mb-4">
            <div className="mb-0 -mt-4 flex justify-center w-full overflow-visible">
              <SwyperLogo width={300} height={280} />
            </div>

            {/* User Counter */}
            {userCount !== null && (
              <div className="mb-4 -mt-2">
                <div className="flex items-center gap-2 justify-center">
                  <span className="text-xl">✨</span>
                  <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 text-base">
                    {userCount.toLocaleString()}
                  </span>
                  <span className="text-gray-600 text-base">
                    {userCount === 1 ? 'person' : 'people'} swyping
                  </span>
                  <span className="text-xl">✨</span>
                </div>
              </div>
            )}
            
            <p className="text-gray-600 font-bold text-xl mb-4">
              {step === 'email' ? 'Welcome to Swyper!' : 'Welcome back!'}
            </p>
          </div>

          {/* Email Step */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Checking...
                  </div>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          )}

          {/* Password Step (Existing User) */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-display">Email</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="email-display"
                    type="email"
                    value={email}
                    disabled
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="text-purple-600 hover:text-purple-700"
                    title="Change email"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => window.location.href = '/forgot-password'}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>
          )}

          {step === 'password' && (
            <div className="mt-4 text-center">
              <button
                onClick={handleBackToEmail}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                Not you? Use a different email
              </button>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          By continuing, you agree to Swyper's Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default UnifiedAuth;
